import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useMatches } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectStudyPhase,
  setStudyPhase,
  StudyPhase,
} from '../slices/study/studySlice';
import {
  exitStandalone,
  selectStudyContext,
  setReturnTo,
} from '../slices/studyContext/studyContextSlice';

type StudyHandle = {
  studyPhase?: StudyPhase;
};

export const StudyPhaseRouteSync = ({ children }: { children: ReactNode }) => {
  const matches = useMatches();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentPhase = useSelector(selectStudyPhase);
  const studyContext = useSelector(selectStudyContext);
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    const matchWithPhase = [...matches]
      .reverse()
      .find((match) => (match.handle as StudyHandle | undefined)?.studyPhase);

    const nextPhase = (matchWithPhase?.handle as StudyHandle | undefined)
      ?.studyPhase;

    const resolvedPhase = nextPhase ?? StudyPhase.HOME;
    if (resolvedPhase !== currentPhase) {
      dispatch(setStudyPhase(resolvedPhase));
    }
  }, [matches, currentPhase, dispatch]);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const isStudyPath = location.pathname.startsWith('/study');
    if (studyContext.mode === 'standalone' && !isStudyPath) {
      if (previousPath !== location.pathname) {
        dispatch(exitStandalone());
      }
    } else if (studyContext.returnTo === 'documents' && !isStudyPath) {
      // A Documents video sets returnTo='documents' in session mode (not
      // standalone). Clear it on leaving /study so the stale value can't
      // misroute the next Home session. Scoped to 'documents' only, to leave
      // every existing session-return flow (topics/video/flashcard) untouched.
      if (previousPath !== location.pathname) {
        dispatch(setReturnTo(null));
      }
    }
    previousPathRef.current = location.pathname;
  }, [dispatch, location.pathname, studyContext.mode, studyContext.returnTo]);

  return <>{children}</>;
};
